import {
  Box,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  Input,
  InputProps,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  colors,
  padZeros,
  Signature,
  SignatureBody,
  SignatureField,
  SignatureType,
  SignatureTypedBody,
  WithoutHash,
} from '@hb/shared'
import {
  CSSProperties,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { InputRef } from '../types'

// const isValidYear = (year: string) => {
//   const yearNum = Number.parseInt(year, 10)
//   if (Number.isNaN(yearNum)) return false
//   return yearNum >= 1900 && yearNum <= 2999
// }

// const isValidMonth = (month: string) => {
//   const monthNum = Number.parseInt(month, 10)
//   if (Number.isNaN(monthNum)) return false
//   return monthNum >= 1 && monthNum <= 12
// }

// const daysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate()

// const isValidDay = (day: string, month: string, year: string) => {
//   const dayNum = Number.parseInt(day, 10)
//   const monthNum = Number.parseInt(month, 10)
//   const yearNum = Number.parseInt(year, 10)
//   if (Number.isNaN(dayNum) || Number.isNaN(monthNum) || Number.isNaN(yearNum)) return false
//   const daysInThisMonth = daysInMonth(yearNum, monthNum)
//   return dayNum >= 1 && dayNum <= daysInThisMonth
// }

interface DateSegmentInputProps {
  value: string
  onChange: (updated: string) => void
  disabled?: boolean
  readOnly?: boolean
  width: number
  placeholder: string
  maxLength: number
  onBlur?: () => void
  onFocus?: () => void
}
const DateSegmentInput = forwardRef<HTMLInputElement, DateSegmentInputProps>(
  (
    { value, onChange, disabled, readOnly, maxLength, width, placeholder, onBlur, onFocus },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const [isHovered, setIsHovered] = useState(false)

    let borderColor = 'gray.200'
    if (isFocused) borderColor = 'green.500'
    else if (isHovered) borderColor = 'gray.300'

    return (
      <Flex
        alignItems="center"
        // borderRadius="md"
        borderBottomWidth={1}
        borderColor={borderColor}
        width={`${width}px`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        <Input
          type="text"
          value={value}
          ref={ref}
          onChange={e => onChange(e.target.value)}
          onFocus={() => {
            if (onFocus) onFocus()
            setIsFocused(true)
          }}
          _focus={{
            borderColor: colors.green.hex,
            boxShadow: 'none',
          }}
          size="xs"
          colorScheme="green"
          pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
          onBlur={() => {
            if (onBlur) onBlur()
            setIsFocused(false)
          }}
          disabled={disabled}
          maxLength={maxLength}
          placeholder={placeholder}
          readOnly={readOnly}
          style={{
            border: 'none',
            fontSize: '0.9rem',
            textAlign: 'center',
            padding: '0.08rem 0',
            outline: 'none',
            width: '100%',
            background: 'none',
          }}
        />
      </Flex>
    )
  },
)

interface SignatureDateInputProps {
  value: string
  onChange: (updated: string) => void
  // onError: (error: string) => void
  disabled?: boolean
  readOnly?: boolean
  onFocus: () => void
}
// split date into three inputs, value formatted as 'MM/DD/YYYY'
const SignatureDateInput = forwardRef<InputRef, SignatureDateInputProps>(
  ({ value, onChange, disabled, readOnly, onFocus }, ref) => {
    // const { todayString } = useTodayDate()

    const [lastActiveSection, setLastActiveSection] = useState<'month' | 'day' | 'year' | null>(
      null,
    )
    const monthInput = useRef<HTMLInputElement>(null)
    const dayInput = useRef<HTMLInputElement>(null)
    const yearInput = useRef<HTMLInputElement>(null)

    const toNext = useCallback(() => {
      switch (lastActiveSection) {
        case 'month':
          dayInput.current?.focus()
          return true
        case 'day':
          yearInput.current?.focus()
          return true
        case 'year':
          return false
        default:
          return false
      }
    }, [lastActiveSection])

    useImperativeHandle(ref, () => ({
      focus: () => {
        monthInput.current?.focus()
      },
      blur: () => {
        monthInput.current?.blur()
        dayInput.current?.blur()
        yearInput.current?.blur()
      },
      toNext,
    }))

    const handleSectionFocus = useCallback(
      (name: 'month' | 'day' | 'year') => {
        onFocus()
        setLastActiveSection(name)
      },
      [onFocus],
    )

    const [month, setMonth] = useState(value?.split('/')[0] ?? '')
    const [day, setDay] = useState(value?.split('/')[1] ?? '')
    const [year, setYear] = useState(value?.split('/')[2] ?? '')
    const monthRef = useRef(month)
    const dayRef = useRef(day)
    const yearRef = useRef(year)

    const updateValue = useCallback(() => {
      // if (
      //   isValidYear(yearRef.current)
      //   && isValidMonth(monthRef.current)
      //   && isValidDay(dayRef.current, monthRef.current, yearRef.current)
      // ) {
      onChange(`${monthRef.current}/${dayRef.current}/${yearRef.current}`)
      // }
    }, [onChange])

    // const error = useMemo(
    //   () => (value && value !== todaysDate ? "Should be today's date" : ''),
    //   [value, todaysDate],
    // )

    // useEffect(() => {
    //   const newError = value && value !== todayString && !readOnly ? "Should be today's date" : ''
    //   onError(newError)
    // }, [value, todayString, onError, readOnly])

    const updateMonth = useCallback(
      (newMonth: string) => {
        monthRef.current = newMonth
        setMonth(newMonth)
        updateValue()
      },
      [updateValue],
    )

    const updateDay = useCallback(
      (newDay: string) => {
        dayRef.current = newDay
        setDay(newDay)
        updateValue()
      },
      [updateValue],
    )

    const updateYear = useCallback(
      (newYear: string) => {
        yearRef.current = newYear
        setYear(newYear)
        updateValue()
      },
      [updateValue],
    )

    // if month, clamp to 1-12 and add leading zero if needed
    const normalizeMonth = useCallback(() => {
      if (!month) return
      const monthNum = Number.parseInt(month, 10)
      if (Number.isNaN(monthNum)) return
      let newMonth = padZeros(monthNum, 2)
      if (monthNum < 1) newMonth = '01'
      else if (monthNum > 12) newMonth = '12'
      setMonth(newMonth)
      monthRef.current = newMonth
      updateValue()
    }, [month, updateValue])

    // if day, clamp to 1-31 and add leading zero if needed
    const normalizeDay = useCallback(() => {
      if (!day) return
      const dayNum = Number.parseInt(day, 10)
      if (Number.isNaN(dayNum)) return
      let newDay = padZeros(dayNum, 2)
      if (dayNum < 1) newDay = '01'
      else if (dayNum > 31) newDay = '31'
      setDay(newDay)
      dayRef.current = newDay
      updateValue()
    }, [day, updateValue])

    // if year, clamp to 1900-2999
    const normalizeYear = useCallback(() => {
      if (!year) return
      const yearNum = Number.parseInt(year, 10)
      if (Number.isNaN(yearNum)) return
      let newYear = yearNum.toString()
      if (yearNum < 1900) newYear = '1900'
      else if (yearNum > 2999) newYear = '2999'
      setYear(newYear)
      yearRef.current = newYear
      updateValue()
    }, [year, updateValue])

    return (
      <Flex
        border={readOnly ? 'none' : '1px solid #cdcdcd'}
        px={2}
        py={1}
        borderRadius={4}
        justify="center"
        flexFlow="column"
        align="center">
        <Text fontSize="sm" color="gray.500" textAlign="center" style={{ userSelect: 'none' }}>
          Date
        </Text>
        <HStack justify="center" spacing={1} w="100%">
          <DateSegmentInput
            value={month}
            onChange={updateMonth}
            width={40}
            onBlur={normalizeMonth}
            ref={monthInput}
            onFocus={() => handleSectionFocus('month')}
            disabled={disabled}
            readOnly={readOnly}
            placeholder="MM"
            maxLength={2}
          />
          <DateSegmentInput
            value={day}
            onChange={updateDay}
            onFocus={() => handleSectionFocus('day')}
            ref={dayInput}
            disabled={disabled}
            onBlur={normalizeDay}
            width={40}
            readOnly={readOnly}
            placeholder="DD"
            maxLength={2}
          />
          <DateSegmentInput
            value={year}
            onChange={updateYear}
            onBlur={normalizeYear}
            onFocus={() => setLastActiveSection('year')}
            ref={yearInput}
            disabled={disabled}
            readOnly={readOnly}
            width={60}
            placeholder="YYYY"
            maxLength={4}
          />
        </HStack>
        {/* <CollapseError error={error} /> */}
      </Flex>
    )
  },
)

const SignaturePrintInput = forwardRef<
  HTMLInputElement,
  InputProps & { readOnly?: boolean; noScript?: boolean }
>(({ noScript, ...props }, ref) => {
  const useScriptFont = useMemo(() => props.value && !noScript, [props.value, noScript])
  return (
    <Input
      ref={ref}
      type="text"
      size="md"
      borderColor="#cdcdcd"
      borderWidth="1px"
      fontSize={useScriptFont ? 'xl' : 'md'}
      borderTopWidth={props.readOnly ? 0 : '1px'}
      borderRightWidth={props.readOnly ? 0 : '1px'}
      borderLeftWidth={props.readOnly ? 0 : '1px'}
      fontFamily={useScriptFont ? 'Dancing Script' : 'Open Sans'}
      // border={readOnly ? 'none' : '1px solid #cdcdcd'}
      _focus={{
        borderColor: colors.green.hex,
        boxShadow: 'none',
      }}
      {...props}
    />
  )
})

const LegalNameDifferentCheckbox = ({
  value,
  onChange,
  readOnly,
}: {
  value: boolean
  onChange: (updated: boolean) => void
  readOnly?: boolean
}) => (
  <Box
    pointerEvents={readOnly ? 'none' : 'auto'}
    onClick={e => {
      e.stopPropagation()
      onChange(!value)
    }}
    py={1}
    px={2}>
    <Checkbox pointerEvents="none" isChecked={value} colorScheme="green" readOnly={readOnly}>
      <Text whiteSpace="nowrap" position="relative" top="2px" fontFamily="Hero-New" fontSize="sm">
        My legal name is different than my name on file
      </Text>
    </Checkbox>
  </Box>
)

const SignaturePrintBodyInput = forwardRef<
  SignatureBodyInputRef,
  {
    value: SignatureTypedBody | null
    onChange: (updated: SignatureTypedBody | null) => void
    onFocus: () => void
    // onError: (error: string | null) => void
    readOnly?: boolean
    field: SignatureField
  }
>(
  (
    {
      value,
      onChange,
      // onError,
      readOnly,
      field,
      onFocus,
    },
    ref,
  ) => {
    const updateField = useCallback(
      (key: keyof SignatureTypedBody, updated: any) => {
        if (readOnly) return
        const newBody: SignatureTypedBody = {
          type: 'typed',
          firstName: value?.firstName ?? '',
          lastName: value?.lastName ?? '',
          middleInitial: value?.middleInitial ?? '',
          differentLegalName: value?.differentLegalName ?? false,
          differentNameExplanation: value?.differentNameExplanation ?? '',
          [key]: updated,
        }
        onChange(newBody)
      },
      [onChange, value, readOnly],
    )

    const fnameRef = useRef<HTMLInputElement>(null)
    const mInitialRef = useRef<HTMLInputElement>(null)
    const lnameRef = useRef<HTMLInputElement>(null)

    const showMiddleInitial = useMemo(() => {
      if (readOnly) return !!value?.middleInitial
      return !!field.expectedMiddleInitial || value?.differentLegalName
    }, [field, value, readOnly])

    const [lastActiveSection, setLastActiveSection] = useState<
      'fname' | 'mInitial' | 'lname' | null
    >(null)

    const toNext = useCallback(() => {
      switch (lastActiveSection) {
        case 'fname':
          if (showMiddleInitial) mInitialRef.current?.focus()
          else lnameRef.current?.focus()
          return true
        case 'mInitial':
          lnameRef.current?.focus()
          return true
        default:
          return false
      }
    }, [lastActiveSection, showMiddleInitial])

    const handleSectionFocus = useCallback(
      (name: 'fname' | 'mInitial' | 'lname') => {
        onFocus()
        setLastActiveSection(name)
      },
      [onFocus],
    )

    useImperativeHandle(ref, () => ({
      focus: () => {
        fnameRef.current?.focus()
      },
      blur: () => {
        fnameRef.current?.blur()
      },
      getFocused: () => lastActiveSection,
      toNext,
    }))

    const showLegalNameDifferentCheckbox = useMemo(() => {
      if (!readOnly) return true
      if (value?.differentLegalName) return true
      if (readOnly) return !value
      return false
    }, [value, readOnly])

    const showLegalNameExplanation = useMemo(() => {
      if (readOnly) return !value || !!value?.differentLegalName
      return !!value?.differentLegalName
    }, [value, readOnly])

    return (
      <Flex w="100%" flexFlow="column">
        <HStack pb={1} spacing={1} w="100%">
          <SignaturePrintInput
            placeholder="Legal First Name"
            ref={fnameRef}
            readOnly={readOnly}
            value={value?.firstName ?? ''}
            onChange={e => updateField('firstName', e.target.value)}
            onFocus={() => handleSectionFocus('fname')}
          />
          {showMiddleInitial ? (
            <SignaturePrintInput
              placeholder="M"
              width="68px"
              readOnly={readOnly}
              ref={mInitialRef}
              maxLength={1}
              value={value?.middleInitial ?? ''}
              onChange={e => updateField('middleInitial', e.target.value)}
              onFocus={() => handleSectionFocus('mInitial')}
            />
          ) : null}
          <SignaturePrintInput
            readOnly={readOnly}
            ref={lnameRef}
            placeholder="Legal Last Name"
            value={value?.lastName ?? ''}
            onChange={e => updateField('lastName', e.target.value)}
            onFocus={() => handleSectionFocus('lname')}
          />
        </HStack>
        {showLegalNameDifferentCheckbox ? (
          <>
            <LegalNameDifferentCheckbox
              value={!!value?.differentLegalName}
              readOnly={readOnly}
              onChange={checked => {
                updateField('differentLegalName', checked)
              }}
            />
            <Collapse in={showLegalNameExplanation} style={{ width: '100%' }}>
              <Box width="100%" pb={2}>
                <SignaturePrintInput
                  noScript
                  readOnly={readOnly}
                  placeholder="Please explain the name difference"
                  value={value?.differentNameExplanation ?? ''}
                  onChange={e => updateField('differentNameExplanation', e.target.value)}
                />
              </Box>
            </Collapse>
          </>
        ) : null}

        {/* <CollapseError error={error} /> */}
      </Flex>
    )
  },
)

interface SignatureBodyInputRef extends InputRef {
  getFocused: () => 'fname' | 'mInitial' | 'lname' | null
}
const SignatureBodyInput = forwardRef<
  SignatureBodyInputRef,
  {
    value: SignatureBody | null | undefined
    onChange: (updated: SignatureBody | null) => void
    // onError: (error: string | null) => void
    readOnly?: boolean
    method: SignatureType
    field: SignatureField
    onFocus: () => void
  }
>(
  (
    {
      value,
      onChange,
      // onError,
      method,
      readOnly,
      field,
      onFocus,
    },
    ref,
  ) => (
    <VStack>
      <Collapse in={method === 'typed'} style={{ width: '100%' }}>
        <SignaturePrintBodyInput
          ref={ref}
          value={value as SignatureTypedBody}
          field={field}
          onChange={onChange}
          readOnly={readOnly}
          onFocus={onFocus}
          // onError={onError}
        />
      </Collapse>
      <Collapse in={method === 'drawn'} style={{ width: '100%' }}></Collapse>
    </VStack>
  ),
)

export const SignatureInputBody = forwardRef<
  InputRef,
  {
    value: Signature | WithoutHash<Signature> | undefined | null
    field: SignatureField
    onChange: (updated: WithoutHash<Signature> | undefined | null) => void
    disabled?: boolean
    readOnly?: boolean
    onFocus?: () => void
    onBlur?: () => void
    style?: CSSProperties
  }
>(({ value, onChange, disabled, readOnly, onFocus, onBlur, style, field }, ref) => {
  const [method] = useState<SignatureType>(value?.body?.type || 'typed')
  const [lastActiveSection, setLastActiveSection] = useState<'body' | 'date' | null>(null)
  // const [dateString, setDateString] = useState<string | null>(value?.date ?? null)

  // const dateStringRef = useRef(dateString)

  // const [signatureBody, setSignatureBody] = useState<SignatureBody | null>(value?.body ?? null)
  // const signatureBodyRef = useRef<SignatureBody | null>(signatureBody)

  const onDateChange = useCallback(
    (newDate: string | null) => {
      // setDateString(newDate)
      // dateStringRef.current = newDate
      onChange({ body: value?.body, date: newDate ?? '' } as Signature)
    },
    [onChange, value],
  )

  const onSignatureBodyChange = useCallback(
    (newSignatureBody: SignatureBody | null) => {
      // setSignatureBody(newSignatureBody)
      // signatureBodyRef.current = newSignatureBody
      onChange({ body: newSignatureBody, date: value?.date ?? '' } as Signature)
    },
    [onChange, value],
  )

  const bodyRef = useRef<SignatureBodyInputRef>(null)
  const dateRef = useRef<InputRef>(null)

  const toNext = useCallback(() => {
    if (lastActiveSection === 'body') {
      const currBody = bodyRef.current?.getFocused()
      if (currBody === 'lname') {
        dateRef.current?.focus()
        return true
      } else {
        const bodyNext = bodyRef.current?.toNext?.()
        if (bodyNext) return true
      }
    }
    return dateRef.current?.toNext?.() ?? false
  }, [lastActiveSection])

  useImperativeHandle(ref, () => ({
    focus: () => {
      bodyRef.current?.focus()
    },
    blur: () => {
      bodyRef.current?.blur()
      dateRef.current?.blur()
    },
    toNext,
  }))

  return (
    <Flex
      onFocus={onFocus}
      onBlur={onBlur}
      minW="300px"
      flexFlow="column"
      w="100%"
      px={2}
      py={1}
      style={style}>
      <Flex align="center" gap={2} w="100%">
        <Box flex={1}>
          <SignatureBodyInput
            ref={bodyRef}
            method={method}
            readOnly={readOnly}
            field={field}
            onChange={onSignatureBodyChange}
            // onError={err => setSignatureError(err)}
            value={value?.body}
            onFocus={() => setLastActiveSection('body')}
          />
        </Box>
      </Flex>
      <SignatureDateInput
        value={value?.date ?? ''}
        onChange={onDateChange}
        ref={dateRef}
        // onError={err => setDateError(err)}
        disabled={disabled}
        readOnly={readOnly}
        onFocus={() => setLastActiveSection('date')}
      />
      {/* <CollapseError error={error} /> */}
    </Flex>
  )
})
